import React, { useEffect, useState } from 'react';
import { Card, Form, Row, Col, Button, Spinner, Alert } from 'react-bootstrap';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import { toast } from 'react-toastify';
import axiosInstance from '../../../api/axios';

const SizeUpgrade = ({ levels, couponsData }) => {
    const accessToken = localStorage.getItem('authToken');
    const animatedComponents = makeAnimated();
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [inputData, setInputData] = useState({
        level: '',
        type: '',
        interval: '',
        value: '',
        numberOfTimes: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [filteredLevels, setFilteredLevels] = useState([]);
    const [couponsList, setCouponsList] = useState([]);
    const [isFormValid, setIsFormValid] = useState(false);

    useEffect(() => {
        setIsFormValid(
            inputData.level !== '' && 
            inputData.type !== '' && 
            (inputData.type === 'REWARD' || selectedOptions.length > 0) &&
            inputData.interval !== '' && 
            inputData.numberOfTimes !== ''
        );
    }, [inputData, selectedOptions]);

    const filterLevels = () => {
        const level = levels?.filter(item => item.sequenceNo !== 1);
        setFilteredLevels(level);
    };

    const prepareCouponsList = () => {
        const list = couponsData
            .filter(item => item.sizeUpgradeAddon != null)
            .map(item => ({
                value: item._id,
                label: `${item.title} (${item.couponCode})`
            }));
        setCouponsList(list);
    };

    const customStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: '48px',
            borderRadius: '6px',
            borderColor: '#ced4da',
            '&:hover': {
                borderColor: '#80bdff',
            },
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#6c757d',
            fontSize: '1rem',
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999,
        }),
    };

    const handleSelectChange = (selected) => {
        setSelectedOptions(selected);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setInputData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const selectedCoupons = selectedOptions.map(option => option.value);

            const data = {
                level: inputData.level,
                type: inputData.type,
                applyingConditions: {
                    interval: inputData.interval,
                    value: inputData.value,
                    numberOfTimes: inputData.numberOfTimes
                },
                couponData: selectedCoupons
            };

            if (data.type === 'REWARD') delete data.couponData;
            if (data.applyingConditions.value === 'null') delete data.applyingConditions.value;

            const response = await axiosInstance.post('level-reward', data, { headers });
            if (response?.data?.status) {
                toast.success(response.data.data.message, {
                    position: 'top-right',
                    autoClose: 3000,
                    theme: 'colored',
                });
                setInputData({
                    level: '',
                    type: '',
                    interval: '',
                    value: '',
                    numberOfTimes: ''
                });
                setSelectedOptions([]);
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.error?.message || 
                               error?.response?.data?.error || 
                               "Something went wrong";
            toast.error(errorMessage, {
                position: 'top-right',
                autoClose: 3000,
                theme: 'colored',
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        filterLevels();
        prepareCouponsList();
    }, [levels, couponsData]);

    return (
        <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom-0 pb-0">
                <Row>
                    <Col className='p-1'>
                        <div className='d-flex'>
                            <h5 className="fw-bold">SIZE UPGRADE</h5>
                            <p className="text-muted mb-0">(Configure size upgrade rewards for loyalty levels)</p>
                        </div>
                    </Col>
                    <hr className='mt-0' />
                </Row>
                
            </Card.Header>
            <Form onSubmit={handleSubmit}>
                <Card.Body className="pt-0">
                    <Row className="gy-3">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-semibold">
                                    Level <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Select
                                    name="level"
                                    value={inputData.level}
                                    onChange={handleChange}
                                    className="form-select"
                                >
                                    <option value="">Select level</option>
                                    {filteredLevels.map(level => (
                                        <option key={level._id} value={level._id}>
                                            {level.title}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-semibold">
                                    Reward Type <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Select
                                    name="type"
                                    value={inputData.type}
                                    onChange={handleChange}
                                    className="form-select"
                                >
                                    <option value="">Select type</option>
                                    <option value="COUPON">Coupon</option>
                                    <option value="REWARD">Reward</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={12}>
                            <Form.Group>
                                <Form.Label className="fw-semibold">
                                    Coupons <span className="text-danger">*</span>
                                </Form.Label>
                                <Select
                                    isMulti
                                    components={animatedComponents}
                                    value={selectedOptions}
                                    onChange={handleSelectChange}
                                    options={couponsList}
                                    styles={customStyles}
                                    placeholder="Select coupons..."
                                    isDisabled={inputData.type === 'REWARD'}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <hr className="my-4" />

                    <h6 className="fw-bold mb-3">Applying Conditions</h6>

                    <Row className="gy-3">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-semibold">
                                    Interval <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Select
                                    name="interval"
                                    value={inputData.interval}
                                    onChange={handleChange}
                                    className="form-select"
                                >
                                    <option value="">Select interval</option>
                                    <option value="WEEKLY">Weekly</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-semibold">
                                    Day <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Select
                                    name="value"
                                    value={inputData.value}
                                    onChange={handleChange}
                                    className="form-select"
                                >
                                    <option value="">Select day</option>
                                    <option value="0">Sunday</option>
                                    <option value="1">Monday</option>
                                    <option value="2">Tuesday</option>
                                    <option value="3">Wednesday</option>
                                    <option value="4">Thursday</option>
                                    <option value="5">Friday</option>
                                    <option value="6">Saturday</option>
                                    <option value="null">Any day</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-semibold">
                                    Frequency <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Select
                                    name="numberOfTimes"
                                    value={inputData.numberOfTimes}
                                    onChange={handleChange}
                                    className="form-select"
                                >
                                    <option value="">Select frequency</option>
                                    <option value="1">1 time</option>
                                    <option value="2">2 times</option>
                                    <option value="3">3 times</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
                <Card.Footer className="bg-white border-top-0 pt-0 text-end">
                    <Button 
                        variant="primary" 
                        type="submit" 
                        disabled={!isFormValid || isLoading}
                        className="px-4 py-2"
                    >
                        {isLoading ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" className="me-2" />
                                Saving...
                            </>
                        ) : 'Save Configuration'}
                    </Button>
                </Card.Footer>
            </Form>
        </Card>
    );
};

export default SizeUpgrade;